import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import type { LeakedSearchTermRow } from 'src/context/api/campaignLeaks/api';
import { Accessors } from '../shared/grid-filtering';
import { DASH } from '../shared/format';
import type { CampaignLeaksT } from '../shared/i18n';

export const COLUMNS_STORAGE_KEY = 'campaignLeaks.leaked.columns';

/** Column order of the columns panel (maquette 7827:15931). */
export const LEAKED_FIELDS = [
  'search_term',
  'match_type',
  'campaign_type',
  'region_name',
  'account_name',
  'campaign_name',
  'ad_group_name',
  'keyword',
  'ad_cost',
  'ad_clicks',
] as const;

/** Visible by default: the columns of the maquette's table (decision 4). */
export const DEFAULT_VISIBLE = new Set<string>(['search_term', 'campaign_name', 'ad_group_name', 'keyword', 'ad_cost', 'ad_clicks']);

export const defaultColumnVisibility = (): GridColumnVisibilityModel =>
  LEAKED_FIELDS.reduce((acc, field) => {
    acc[field] = DEFAULT_VISIBLE.has(field);
    return acc;
  }, {} as GridColumnVisibilityModel);

export const DEFAULT_SORT = [{ field: 'ad_cost', sort: 'desc' as const }];

const MATCH_TYPES = ['EXACT', 'PHRASE', 'BROAD'];

/** "ST type" column (decision 5): Exact / Phrase / Broad, — without keyword (Shopping, PMax). */
export const matchTypeLabel = (ct: CampaignLeaksT, matchType: string | null | undefined) =>
  matchType && MATCH_TYPES.includes(matchType) ? ct(`match_types.${matchType}`) : DASH;

/** Keyword written as in Google Ads: [exact], "phrase", broad. Empty when null. */
export const formatKeyword = (keyword: string | null | undefined, matchType: string | null | undefined) => {
  if (!keyword) return '';
  if (matchType === 'EXACT') return `[${keyword}]`;
  if (matchType === 'PHRASE') return `"${keyword}"`;
  return keyword;
};

/** Values filtered and sorted on; the match type in the user's language, as displayed. */
export const getLeakedAccessors = (ct: CampaignLeaksT): Accessors<LeakedSearchTermRow> => ({
  search_term: (row) => row.search_term,
  match_type: (row) => matchTypeLabel(ct, row.keyword_match_type),
  campaign_type: (row) => row.campaign_type,
  region_name: (row) => row.region_name ?? DASH,
  account_name: (row) => row.account_name,
  campaign_name: (row) => row.campaign_name,
  ad_group_name: (row) => row.ad_group_name,
  keyword: (row) => row.keyword ?? '',
  ad_cost: (row) => row.ad_cost ?? 0,
  ad_clicks: (row) => row.ad_clicks ?? 0,
});

/** Values filtered on: as sorted, but the keyword as displayed ([exact], "phrase", broad). */
export const getLeakedFilterAccessors = (ct: CampaignLeaksT): Accessors<LeakedSearchTermRow> => ({
  ...getLeakedAccessors(ct),
  keyword: (row) => formatKeyword(row.keyword, row.keyword_match_type),
});

/** Distinct values of a column, for its multiple-choice filter. */
export const distinctValues = (
  rows: LeakedSearchTermRow[],
  accessors: Accessors<LeakedSearchTermRow>,
  field: string
) => Array.from(new Set(rows.map((row) => String(accessors[field](row) ?? '')))).sort((a, b) => a.localeCompare(b));

/** CSV: every column (visible or not), raw values, keyword without brackets + match type column. */
export const CSV_FIELDS = [
  'search_term',
  'keyword_match_type',
  'campaign_type',
  'region_name',
  'account_name',
  'campaign_name',
  'ad_group_name',
  'keyword',
  'ad_cost',
  'ad_clicks',
];

export const csvRows = (rows: LeakedSearchTermRow[]) =>
  rows.map((row) => ({
    search_term: row.search_term,
    keyword_match_type: row.keyword_match_type ?? '',
    campaign_type: row.campaign_type,
    region_name: row.region_name ?? '',
    account_name: row.account_name,
    campaign_name: row.campaign_name,
    ad_group_name: row.ad_group_name,
    keyword: row.keyword ?? '',
    ad_cost: row.ad_cost,
    ad_clicks: row.ad_clicks,
  }));

export const csvFilename = (workspace: string, platform: string, end: string | undefined) =>
  `campaign-leaks_leaked-search-terms_${workspace}_${platform}_${end ?? ''}.csv`;
